import _ from 'lodash';
import { quoteInstallationLocationType } from '../quotes/quoteInstallationInformation';
import { SubLineItem } from '../subLineItem';
import { roundNumberToNearestCent } from '@ami/react/core';
import { getQuoteProductLeadTime } from '../quotes/lead-times/leadTimeCalculator';
import { apiFetch } from '@ami/react/core';
import { DealerBranchSummary } from '../dealer-branches/dealerBranch';
import { PriceZoneSummary } from '../price-zones/priceZone';
import { ProductGroupDimensionVisibilityType } from '../products/productGroup';
import QuoteProductDetail from '../quotes/entities/quoteProductDetail';

const quoteCreationType = Object.freeze({
  unknown: 0,
  byMakeAndModel: 1,
  byWeightClass: 2,
});

const quoteProductType = Object.freeze({
  allProducts: 1,
  inventoryOnly: 2,
});

const quoteProductLeadTimeType = Object.freeze({
  buildToOrderCall: 'BuildToOrderCall',
  buildToOrderCustom: 'BuildToOrderCustom',
  buildToOrderStandard: 'BuildToOrderStandard',
  inventory: 'Inventory',
  inventoryCustom: 'InventoryCustom',
});

class CreateQuoteStep1Settings {
  constructor(json) {
    if (json) {
      this.dealerGroupId = json.dealerGroupId;
      this.dealerGroupName = json.dealerGroupName;
      this.dealerBranchSummary = json.dealerBranchSummary;
      return;
    }
    this.dealerGroupId = null;
    this.dealerGroupName = null;
    this.dealerBranchSummary = null;
  }

  static async getForAmiUser() {
    const entity = await apiFetch('quotes/step1/settings/ami');
    return entity && entity !== null
      ? new CreateQuoteStep1Settings(entity)
      : undefined;
  }

  static async getForDealerUser() {
    const entity = await apiFetch('quotes/step1/settings/dealer');
    return entity && entity !== null
      ? new CreateQuoteStep1Settings(entity)
      : undefined;
  }
}

class CreateQuoteStep1 {
  constructor(json) {
    if (json) {
      this.dealerGroupId = json.dealerGroupId;
      this.dealerGroupName = json.dealerGroupName;
      this.dealerBranchId = json.dealerBranchId;
      this.dealerBranchName = json.dealerBranchName;
      this.carrierId = json.carrierId;
      this.weightCategoryId = json.weightCategoryId;
      this.industryId = json.industryId;
      this.industryName = json.industryName;
      this.machineId = json.machineId;
      this.type = json.type;
      this.manufacturer = json.manufacturer;
      this.model = json.model;
      this.priceZoneId = json.priceZoneId;
      this.dealerBranchSummary =
        json.dealerBranchSummary && json.dealerBranchSummary !== null
          ? new DealerBranchSummary(json.dealerBranchSummary)
          : null;
      this.priceZoneSummary =
        json.priceZoneSummary && json.priceZoneSummary !== null
          ? new PriceZoneSummary(json.priceZoneSummary)
          : null;
      return;
    }
    this.dealerGroupId = null;
    this.dealerGroupName = null;
    this.dealerBranchId = null;
    this.dealerBranchName = null;
    this.carrierId = null;
    this.weightCategoryId = null;
    this.industryId = undefined;
    this.industryName = null;
    this.machineId = null;
    this.type = quoteCreationType.unknown;
    this.manufacturer = null;
    this.model = '';
    this.priceZoneId = null;
  }

  getHeaderLine1(productConfiguration) {
    if (this.manufacturer && this.model)
      return `${this.manufacturer} ${this.model}`;

    const carrier = productConfiguration.carriers.find(
      (x) => x.id === this.carrierId
    );
    return carrier.name;
  }

  getHeaderLine2_A(productConfiguration) {
    const carrier = productConfiguration.carriers.find(
      (x) => x.id === this.carrierId
    );
    const weightClass = carrier.weightCategories.find(
      (x) => x.id === this.weightCategoryId
    );

    return `Category ${weightClass.name}`;
  }

  getHeaderLine2_B(productConfiguration) {
    const carrier = productConfiguration.carriers.find(
      (x) => x.id === this.carrierId
    );
    const weightClass = carrier.weightCategories.find(
      (x) => x.id === this.weightCategoryId
    );

    return `(${weightClass.description})`;
  }

  static getFromOrderData(machineInfo, dealerBranchSummary, priceZoneSummary) {
    return new CreateQuoteStep1({
      dealerGroupId: dealerBranchSummary.dealerGroupId,
      dealerBranchId: dealerBranchSummary.dealerBranchId,
      priceZoneId: priceZoneSummary.priceZoneId,
      carrierId: machineInfo.carrierId,
      type: quoteCreationType.byMakeAndModel,
      machineId: machineInfo.machineId,
      weightCategoryId: machineInfo.weightCategoryId,
      dealerBranchSummary,
      priceZoneSummary,
    });
  }
}

const QuoteStatus =  Object.freeze({
  PoBlocked: 'PO Blocked',
  Pending: 'Pending',
  Accepted: 'PO Submitted',
  Completed: 'Completed',
  Expired: 'Expired',
  Cancelled: 'Cancelled',
})

class QuoteProductDto {
  constructor(json) {
    this.id = null;
    this.attachmentPointId = null;
    this.productCategoryId = null;
    this.productGroupId = null;
    this.productId = null;
    this.inventoryItemId = null;
    this.productGroupImageUrl = null;
    this.quantity = 1;
    this.productOptions = [];
    this.standaloneComponents = [];
    this.customizations = [];
    this.dNetPrice = 0;
    this.msrp = 0;
    this.step3Price = 0;
    this.step3Msrp = 0;
    this.buildToOrderLeadTimeOverride = null;
    this.inventoryLeadTimeOverride = null;
    this.dimensions = [];
    this.notes = '';
    this.manufacturingInstructions = '';
    this.requiresReview = false;
    this.isReviewed = false;
    this.paintColor = '';
    this.leadTime = null;
    this.leadTimeOverrides = [];
    this.leadTimeOverridesReason = [];
    this.totalLeadTimeInDays = null; // UI Only
    this.originalProduct = null;
    this.allOptionsConfigured = false;
    this.detail = null;
    this.price = null;

    if (json) {
      for (const [key, value] of Object.entries(json)) {
        if (value) {
          this[key] = value;
        }
      }
      if (json.detail) this.detail = new QuoteProductDetail(json.detail);
    }
  }

  toQueryString() {
    return Object.getOwnPropertyNames(this)
      .filter((x) => this[x])
      .map((x) => {
        if (Array.isArray(this[x])) {
          return this[x].map((y) => `${x}=${y}`).join('&');
        } else {
          return `${x}=${this[x]}`;
        }
      })
      .join('&');
  }

  clone() {
    return _.cloneDeep(this);
  }

  isSelectionInProgress(isForSingleAttachmentPoint) {
    return isForSingleAttachmentPoint
      ? this.productCategoryId != null
      : this.attachmentPointId != null;
  }

  updateProductLeadTimeOverride(leadTimeOverrides, leadTimeOverridesReason) {
    this.leadTimeOverrides = leadTimeOverrides;
    this.leadTimeOverridesReason = leadTimeOverridesReason;
  }

  updateLeadTime(leadTime) {
    this.leadTime = leadTime;
  }

  // Used when we get a whole new quote back from the server (during pricing re-calculation)
  updateLeadTimeFromServerQuote(quote) {
    var oldKey = QuoteProductDto.getQuoteProductKey(this);
    var updatedQuoteProduct = quote.products.find((x) => {
      var newKey = QuoteProductDto.getQuoteProductKey(x);
      return oldKey === newKey;
    });

    const oldLeadTime = this.leadTime;
    const newLeadTime = updatedQuoteProduct.leadTime;

    if (
      newLeadTime.type === quoteProductLeadTimeType.buildToOrderCall &&
      oldLeadTime !== quoteProductLeadTimeType.buildToOrderCall
    ) {
      return {
        newLeadTime: undefined,
        oldLeadTime: 99999,
        partNumber: this.partNumber,
      };
    }

    var oldLeadTimeNumberOfDays = oldLeadTime
      ? oldLeadTime.numberOfDays
      : 99999;
    if (newLeadTime.numberOfDays !== oldLeadTimeNumberOfDays) {
      return {
        newLeadTime: newLeadTime.numberOfDays,
        oldLeadTime: oldLeadTimeNumberOfDays,
        partNumber: this.partNumber,
      };
    }
  }

  // Used when we update only the quote product configuration (during Step 3 when selecting inventory)
  updateLeadTimeFromProductConfiguration(quoteProductConfiguration) {
    var oldQuoteProduct = this.clone();
    this.leadTime = getQuoteProductLeadTime(this, quoteProductConfiguration);

    const oldLeadTime = oldQuoteProduct.leadTime;
    const newLeadTime = this.leadTime;

    if (
      newLeadTime.type === quoteProductLeadTimeType.buildToOrderCall &&
      oldLeadTime !== quoteProductLeadTimeType.buildToOrderCall
    ) {
      return {
        newLeadTime: undefined,
        oldLeadTime: 99999,
        partNumber: this.partNumber,
      };
    }

    var oldLeadTimeNumberOfDays = oldLeadTime
      ? oldLeadTime.numberOfDays
      : 99999;
    if (newLeadTime.numberOfDays !== oldLeadTimeNumberOfDays) {
      return {
        newLeadTime: newLeadTime.numberOfDays,
        oldLeadTime: oldLeadTimeNumberOfDays,
        partNumber: this.partNumber,
      };
    }

    return undefined;
  }

  static getQuoteProductKey(quoteProduct) {
    var key = `${quoteProduct.productId}-${quoteProduct.quantity}`;
    quoteProduct.productOptions.forEach((productOption) => {
      key =
        key +
        `|${productOption.productOptionSetId}-${
          productOption.productOptionId &&
          productOption.productOptionId !== null
            ? productOption.productOptionId
            : 'null'
        }`;
    });
    return key;
  }

  addOverridePrice() {
    this.originalDNetPrice = this.dNetPrice;
  }

  removeOverridePrice(msrpMarkupPercentage) {
    this.dNetPrice = this.originalDNetPrice;
    this.msrp =
      this.dNetPrice > 0
        ? roundNumberToNearestCent(
            (this.dNetPrice * (msrpMarkupPercentage + 100)) / 100
          )
        : 0;
    this.originalDNetPrice = undefined;
  }

  updateOverridePrice(value, msrpMarkupPercentage) {
    this.dNetPrice = value ? parseFloat(value) : 0;
    this.msrp =
      this.dNetPrice > 0
        ? roundNumberToNearestCent(
            (this.dNetPrice * (msrpMarkupPercentage + 100)) / 100
          )
        : 0;
  }
}

class QuoteProduct extends QuoteProductDto {
  constructor(json) {
    super(json);
    this.dealerBranchId = json.dealerBranchId;
    this.priceZoneId = json.priceZoneId;
    this.carrierId = json.carrierId;
    this.weightCategoryId = json.weightCategoryId;
  }
}

class QuoteProductDimension {
  constructor(json) {
    this.id = json.id;
    this.name = json.name;
    this.type = json.type;
    this.value = json.value;
    this.hideFromOnline = json.hideFromOnline;
    this.originalValue = json.originalValue;
    this.category = json.category;
  }

  static getProductDimensions(productGroup, product) {
    return productGroup.dimensions
      .filter(
        (x) =>
          x.visibilityType !== ProductGroupDimensionVisibilityType.NotVisible
      )
      .map((dimension) => {
        const productDimension = product.dimensions.find(
          (x) => x.dimensionId === dimension.dimensionId
        );
        return new QuoteProductDimension({
          id: dimension.dimensionId,
          name: dimension.name,
          type: dimension.type,
          value: productDimension ? productDimension.value : '',
          originalValue: productDimension ? productDimension.originalValue : '',
          category: productDimension ? productDimension.category : '',
        });
      });
  }
}

class QuoteProductCustomization {
  constructor(json) {
    if (json) {
      this.id = json.id
      this.partNumber = json.partNumber;
      this.description = json.description;
      this.dNetPrice = json.dNetPrice;
      this.msrp = json.msrp;
      return;
    }
    this.id = new Date().getTime();
    this.partNumber = '';
    this.description = '';
    this.dNetPrice = '';
  }
  clone() {
    return _.cloneDeep(this);
  }
  setPricing(msrpMarkupPercentage) {
    this.dNetPrice = this.dNetPrice ? parseFloat(this.dNetPrice) : 0;
    this.msrp =
      this.dNetPrice > 0
        ? roundNumberToNearestCent(
            (this.dNetPrice * (msrpMarkupPercentage + 100)) / 100
          )
        : 0;
  }
}

class QuoteProductOverrideDiscount {
  constructor(json) {
    if (json) {
      this.name = json.name;
      this.percentage = json.percentage;
      return;
    }
    this.name = '';
    this.percentage = '';
  }
  clone() {
    return _.cloneDeep(this);
  }
}

class QuoteCustomLineItem {
  constructor() {
    this.id = new Date().getTime();
    this.name = '';
    this.basePartNumber = '';
    this.description = '';
    this.leadTimeDays = '';
    this.dNetPrice = '';
    this.subLineItems = [];
    this.quantity = 1;
  }

  static fromServerEntity(json) {
    return Object.assign(new QuoteCustomLineItem(), json, {
      subLineItems: json.subLineItems.map((x) =>
        SubLineItem.fromServerEntity(x)
      ),
    });
  }

  clone() {
    return _.cloneDeep(this);
  }

  getUnitPrice() {
    let unitPrice = this.dNetPrice ? parseFloat(this.dNetPrice) : 0;
    this.subLineItems.forEach(
      (subLineItem) => (unitPrice = unitPrice + subLineItem.price)
    );
    return unitPrice;
  }

  getTotal() {
    const unitSubTotal = this.getUnitPrice();
    return roundNumberToNearestCent(unitSubTotal * this.quantity);
  }

  getPayload() {
    return { ...this, total: this.pricing.totalPrice };
  }
}

const priceComponentType = Object.freeze({
  basePrice: 'BasePrice',
  priceZoneSurcharge: 'PriceZoneSurcharge',
  dealerDiscount: 'DealerDiscount',
  quantityDiscount: 'QuantityDiscount',   // OBSOLETE
  truckloadDiscount: 'TruckloadDiscount', // OBSOLETE
  discount: 'Discount'
});

const couplerDecision = Object.freeze({
  directPinOn: 'DirectPinOn',
  amiCoupler: 'AmiCoupler',
  existingCoupler: 'ExistingCoupler',
  amiTiltRotator: 'AmiTiltRotator',
  existingTiltRotator: 'ExistingTiltRotator',
  notSpecified: 'NotSpecified',
});

class AttachmentPointBase {
  constructor(machineOemThumbMount) {
    this.machineOemThumbMount = machineOemThumbMount;
    this.boltPattern = null;
    this.boltOnMount = null;
    return;
  }
}

class QuoteAttachmentPoint extends AttachmentPointBase {
  constructor(id, machineOemThumbMount) {
    super(machineOemThumbMount);
    this.id = id;
    this.requiresCouplerDecision = null;
    this.productGroupIdsPerType = [];
    this._initializeCouplerDecision(null);
  }

  static fromServerEntity(json) {
    const newEntity = new QuoteAttachmentPoint();
    newEntity.id = json.id;
    newEntity.couplerDecision = json.couplerDecision;
    newEntity.couplerStyleId = json.couplerStyleId;
    newEntity.couplerOilReady = json.couplerOilReady;
    newEntity.requiresCouplerDecision = json.requiresCouplerDecision;
    newEntity.productGroupIdsPerType = Object.entries(
      json.productGroupIdsPerType
    ).map(([type, productGroupIds]) => ({ type, productGroupIds }));
    newEntity.machineOemThumbMount = json.machineOemThumbMount;
    newEntity.boltPattern = json.boltPattern;
    newEntity.boltOnMount = json.boltOnMount;
    return newEntity;
  }

  _initializeCouplerDecision(couplerDecision) {
    this.couplerDecision = couplerDecision;
    this.couplerStyleId = null;
    this.couplerOilReady = null;
  }

  setDirectPinOn() {
    this._initializeCouplerDecision(couplerDecision.directPinOn);
  }

  setExistingCoupler(couplerStyleId, couplerOilReady) {
    this._initializeCouplerDecision(couplerDecision.existingCoupler);
    this.couplerStyleId = couplerStyleId;
    this.couplerOilReady = couplerOilReady;
  }

  setAmiCoupler(couplerStyleId, couplerOilReady) {
    this._initializeCouplerDecision(couplerDecision.amiCoupler);
    this.couplerStyleId = couplerStyleId;
    this.couplerOilReady = couplerOilReady;
  }

  setExistingTiltRotator(couplerStyleId, couplerOilReady) {
    this._initializeCouplerDecision(couplerDecision.existingTiltRotator);
    this.couplerStyleId = couplerStyleId;
    this.couplerOilReady = couplerOilReady;
  }

  setAmiTiltRotator(couplerStyleId, couplerOilReady) {
    this._initializeCouplerDecision(couplerDecision.amiTiltRotator);
    this.couplerStyleId = couplerStyleId;
    this.couplerOilReady = couplerOilReady;
  }

  setThumbMount(machineOemThumbMount, boltPattern, boltOnMount) {
    this.machineOemThumbMount = machineOemThumbMount;
    this.boltPattern = boltPattern;
    this.boltOnMount = boltOnMount;
  }

  setNotSpecified() {
    this._initializeCouplerDecision(couplerDecision.notSpecified);
  }

  clone() {
    return _.cloneDeep(this);
  }

  updateCouplerInformation(dataItem) {
    this.requiresCouplerDecision = dataItem.requiresCouplerDecision;
    this.productGroupIdsPerType = dataItem.productGroupIdsPerType;
  }
}

class QuoteProductOption {
  constructor(json) {
    this.productOptionSetId = json.productOptionSetId;
    this.productOptionId = json.productOptionId;
    this.dNetPrice = json.dNetPrice;
    this.msrp = json.msrp;
    this.originalDNetPrice = json.originalDNetPrice;
    this.leadTime = json.leadTime;
    this.leadTimeOverride = json.leadTimeOverride;
    this.name = json.name;
  }

  addOverridePrice() {
    this.originalDNetPrice = this.dNetPrice;
  }

  removeOverridePrice(msrpMarkupPercentage) {
    this.dNetPrice = this.originalDNetPrice;
    this.msrp =
      this.dNetPrice > 0
        ? roundNumberToNearestCent(
            (this.dNetPrice * (msrpMarkupPercentage + 100)) / 100
          )
        : 0;
    this.originalDNetPrice = undefined;
  }

  updateOverridePrice(value, msrpMarkupPercentage) {
    this.dNetPrice = value ? parseFloat(value) : 0;
    this.msrp =
      this.dNetPrice > 0
        ? roundNumberToNearestCent(
            (this.dNetPrice * (msrpMarkupPercentage + 100)) / 100
          )
        : 0;
  }
}

class CopyQuoteData {
  constructor(json) {
    if (json) {
      Object.keys(json).forEach((key) => (this[key] = json[key]));
      return;
    }
    this.id = '';
    this.name = '';
    this.notes = '';
    this.manufacturingInstructions = '';
    this.paintColor = '';
    this.dealerBranchId = null;
    this.dealerUserId = null;
  }
}

const defaultTravelExpense = Object.freeze({ dNet: 0, msrp: 0 });

class QuoteInstallation {
  constructor(
    json = {
      included: true,
      locationId: null,
      type: null,
      location: null,
      priceZoneId: null,
      travelExpense: defaultTravelExpense,
      areInstallationOptionsCurrent: true,
    }
  ) {
    this.included = json.included;
    this.locationId = json.locationId;
    this.location = json.location;
    this.type = json.type;
    this.priceZoneId = json.priceZoneId;
    this.travelExpense = json.travelExpense;
    this.areInstallationOptionsCurrent = json.areInstallationOptionsCurrent;
  }

  static fromServerEntity(json) {
    return new QuoteInstallation(json);
  }

  clone() {
    return new QuoteInstallation({ ...this });
  }

  setAmiPlantLocation(locationId) {
    this.type = quoteInstallationLocationType.amiPlant;
    this.locationId = locationId;
    this.location = null;
    this.travelExpense = defaultTravelExpense;
    return this;
  }

  setLocation(
    locationId,
    type = null,
    location = null,
    travelExpense = defaultTravelExpense
  ) {
    this.locationId = locationId;
    this.type = type;
    this.location = location;
    this.travelExpense = travelExpense;
    return this;
  }

  setIncluded(included) {
    this.included = included;
    return this;
  }
}

class QuoteOptInDiscount {
  constructor(json) {
    if (json) {
      this.discountId = json.discountId;
      this.documentationFileKey = json.documentationFileKey;
      this.documentationFileName = json.documentationFileName;
      return;
    }
    this.discountId = '';
    this.documentationFileKey = '';
    this.documentationFileName = '';
  }
}

export {
  CreateQuoteStep1Settings,
  CreateQuoteStep1,
  QuoteProductDto,
  QuoteProduct,
  QuoteProductDimension,
  QuoteCustomLineItem,
  QuoteAttachmentPoint,
  QuoteInstallation,
  QuoteProductOption,
  quoteCreationType,
  quoteProductLeadTimeType,
  couplerDecision,
  priceComponentType,
  quoteProductType,
  QuoteStatus,
  CopyQuoteData,
  SubLineItem,
  QuoteProductCustomization,
  AttachmentPointBase,
  QuoteOptInDiscount,
  QuoteProductOverrideDiscount
};
